/**
 * CEO Command Center - Initiative Types
 * 
 * Type definitions for the Initiative system.
 * Initiatives are user-created projects/workstreams with associated tasks.
 * 
 * @module types/initiative
 * @version 10.1.0
 */

// ============================================
// INITIATIVE TYPES
// ============================================

/**
 * Initiative status options
 */
export type InitiativeStatus = 'Active' | 'Paused' | 'Completed' | 'Archived';

/**
 * Initiative priority levels
 */
export type InitiativePriority = 'Critical' | 'High' | 'Medium' | 'Low';

/**
 * Initiative data model
 */
export interface Initiative {
  /** Unique identifier */
  id: string;
  
  /** Initiative name/title */
  name: string;
  
  /** Optional description */
  description?: string;
  
  /** Owner/lead of the initiative */
  owner?: string;
  
  /** Current status */
  status: InitiativeStatus;
  
  /** Priority level */
  priority: InitiativePriority;
  
  /** Emoji icon for display */
  icon: string;
  
  /** Color for visual distinction */
  color: string;
  
  /** Target completion date */
  targetDate?: string | null;
  
  /** Creation timestamp */
  createdAt: string;
  
  /** Last update timestamp */
  updatedAt: string;
  
  /** Is this a system default initiative */
  isDefault?: boolean;
  
  /** Tags for categorization */
  tags?: string[];
}

/**
 * Task associated with an initiative
 */
export interface InitiativeTask {
  /** Unique identifier */
  id: string;
  
  /** Parent initiative ID */
  initiativeId: string;
  
  /** Task title */
  title: string;
  
  /** Task description */
  description?: string;
  
  /** Task owner */
  owner?: string;
  
  /** Task status */
  status: 'Not Started' | 'In Progress' | 'Done';
  
  /** Task priority */
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  
  /** Target date */
  targetDate?: string | null;
  
  /** Is this a signal task */
  isSignal?: boolean;
  
  /** Creation timestamp */
  createdAt: string;
  
  /** Last update timestamp */
  updatedAt: string;
  
  /** Notes/comments */
  notes?: string;
}

/**
 * Document associated with an initiative
 */
export interface InitiativeDocument {
  /** Unique identifier */
  id: string;
  
  /** Parent initiative ID */
  initiativeId: string;
  
  /** Original filename */
  filename: string;
  
  /** File type/extension */
  fileType: string;
  
  /** Upload timestamp */
  uploadedAt: string;
  
  /** Processing status */
  status: 'pending' | 'processing' | 'processed' | 'failed';
  
  /** Number of tasks extracted */
  extractedTaskCount?: number;
  
  /** Error message if failed */
  error?: string;
  
  /** Raw content (for re-processing) */
  rawContent?: string;
}

/**
 * Initiative with computed metrics
 */
export interface InitiativeWithMetrics extends Initiative {
  /** Total tasks count */
  totalTasks: number;
  
  /** Completed tasks count */
  completedTasks: number;
  
  /** In progress tasks count */
  inProgressTasks: number;
  
  /** Progress percentage (0-100) */
  progress: number;
  
  /** Associated documents count */
  documentCount: number;
}

/**
 * Form data for creating/editing initiatives
 */
export interface InitiativeFormData {
  name: string;
  description?: string;
  owner?: string;
  status: InitiativeStatus;
  priority: InitiativePriority;
  icon: string;
  color: string;
  targetDate?: string | null;
  tags?: string[];
}

/**
 * Default initiative icons
 */
export const INITIATIVE_ICONS = [
  '🤖', '🚀', '💡', '🎯', '📊', '🔧', '💼', '🌟',
  '📈', '🔬', '🎨', '🔒', '🌍', '⚡', '🏆', '📱',
];

/**
 * Default initiative colors
 */
export const INITIATIVE_COLORS = [
  '#E85D4C', // Coral (default)
  '#0EA5E9', // Sky blue
  '#22C55E', // Green
  '#F59E0B', // Amber
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#6366F1', // Indigo
  '#14B8A6', // Teal
];

/**
 * Default AI Automation initiative
 */
export const DEFAULT_INITIATIVE: Initiative = {
  id: 'ai-automation-default',
  name: 'AI Automation',
  description: 'AI and automation initiatives for operational efficiency',
  icon: '🤖',
  color: '#0EA5E9',
  status: 'Active',
  priority: 'High',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  isDefault: true,
};
